import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, History as HistoryIcon, RotateCcw, User } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import type { VersionDetail, VersionListItem } from "../lib/types";
import { PageHeader } from "../components/layout/PageHeader";
import { Skeleton } from "../components/ui/skeleton";
import { EmptyState } from "../components/ui/empty-state";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { formatDate } from "../lib/utils";

export function HistoryPage(): JSX.Element {
  const { slug = "", sku = "" } = useParams<{ slug: string; sku: string }>();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<VersionListItem | null>(null);
  const [rollbackTarget, setRollbackTarget] = useState<VersionListItem | null>(null);

  const { data: versions = [], isLoading, isError, error } = useQuery<VersionListItem[]>({
    queryKey: ["versions", slug, sku],
    queryFn: async () => {
      const r = await api.get<VersionListItem[]>(
        `/api/channels/${slug}/products/${sku}/versions`,
      );
      return r.data;
    },
  });

  const { data: detail } = useQuery<VersionDetail>({
    queryKey: ["version-detail", slug, sku, selected?.scope, selected?.version_num],
    queryFn: async () => {
      const r = await api.get<VersionDetail>(
        `/api/channels/${slug}/products/${sku}/versions/${selected!.scope}/${selected!.version_num}`,
      );
      return r.data;
    },
    enabled: !!selected,
  });

  const rollbackMutation = useMutation({
    mutationFn: async (v: VersionListItem) => {
      const r = await api.post<{ new_version_num: number }>(
        `/api/channels/${slug}/products/${sku}/rollback`,
        { scope: v.scope, version_num: v.version_num },
      );
      return r.data;
    },
    onSuccess: (d) => {
      toast.success(
        `Revertido para v${d.new_version_num}. Clique em "Republicar" na ficha pra sincronizar com WC.`,
      );
      setRollbackTarget(null);
      void qc.invalidateQueries({ queryKey: ["versions", slug, sku] });
      void qc.invalidateQueries({ queryKey: ["product", slug, sku] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <div>
      <PageHeader
        title="Histórico"
        description={`Versões de ${sku} no canal ${slug}`}
        breadcrumb={
          <span>
            <Link to="/" className="hover:underline">
              Home
            </Link>{" "}
            ·{" "}
            <Link to={`/canais/${slug}`} className="hover:underline">
              {slug}
            </Link>{" "}
            ·{" "}
            <Link to={`/canais/${slug}/produtos/${sku}`} className="hover:underline">
              {sku}
            </Link>
          </span>
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <section>
          {isLoading ? (
            <div className="stack">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : isError ? (
            <EmptyState
              icon={<AlertCircle className="h-12 w-12" />}
              title="Erro ao carregar histórico"
              description={(error as Error)?.message ?? ""}
            />
          ) : versions.length === 0 ? (
            <EmptyState
              icon={<HistoryIcon className="h-12 w-12" />}
              title="Sem histórico ainda"
              description="As edições geram versões automaticamente. Edite o produto pra ver aqui."
            />
          ) : (
            <ol className="stack" style={{ borderLeft: ".125rem solid var(--color-line)", paddingLeft: "1rem" }}>
              {versions.map((v) => {
                const isSelected = selected?.scope === v.scope && selected.version_num === v.version_num;
                return (
                  <li key={`${v.scope}-${v.version_num}`} style={{ position: "relative", listStyle: "none" }}>
                    <span
                      aria-hidden
                      style={{
                        position: "absolute",
                        left: "-1.3125rem",
                        top: ".5rem",
                        display: "inline-block",
                        width: ".75rem",
                        height: ".75rem",
                        borderRadius: "50%",
                        background: "var(--color-coral)",
                        border: ".125rem solid var(--color-bg)",
                      }}
                    />
                    <div
                      className="card"
                      style={{
                        borderLeft: `.1875rem solid ${isSelected ? "var(--color-coral)" : "transparent"}`,
                      }}
                    >
                      <div className="spread" style={{ alignItems: "flex-start" }}>
                        <div className="grow">
                          <div className="cluster">
                            <Badge variant={v.scope === "entry" ? "secondary" : "default"}>{v.scope}</Badge>
                            <span className="mono truncate" style={{ fontSize: ".75rem", color: "var(--color-ink-2)" }}>
                              v{v.version_num}
                            </span>
                            {v.revertido_de ? (
                              <Badge variant="outline" style={{ fontSize: ".625rem" }}>
                                rev. de v{v.revertido_de}
                              </Badge>
                            ) : null}
                          </div>
                          <div className="cluster" style={{ marginTop: ".25rem", fontSize: ".75rem", color: "var(--color-ink-2)", fontWeight: 700 }}>
                            <span className="cluster" style={{ gap: ".25rem" }}>
                              <User className="icon" style={{ width: ".75rem", height: ".75rem" }} aria-hidden />
                              {v.author?.name ?? "sistema"}
                            </span>
                            <span>·</span>
                            <span>{formatDate(v.created_at)}</span>
                          </div>
                          {v.changed_fields.length > 0 ? (
                            <div className="clamp-2" style={{ marginTop: ".5rem", fontSize: ".75rem", color: "var(--color-ink)" }}>
                              <span style={{ color: "var(--color-ink-2)" }}>Campos:</span> {v.changed_fields.join(", ")}
                            </div>
                          ) : null}
                        </div>
                        <div className="stack" style={{ gap: ".375rem", flexShrink: 0 }}>
                          <Button size="sm" variant="ghost" onClick={() => setSelected(v)}>
                            Ver
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setRollbackTarget(v)}
                            title="Reverter para esta versão (não publica automaticamente)"
                          >
                            <RotateCcw className="icon" style={{ width: ".75rem", height: ".75rem" }} aria-hidden />
                            Reverter
                          </Button>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </section>

        <section>
          {selected && detail ? (
            <div className="card">
              <div className="cluster" style={{ marginBottom: ".75rem" }}>
                <Badge variant={detail.scope === "entry" ? "secondary" : "default"}>{detail.scope}</Badge>
                <span className="mono" style={{ fontSize: ".875rem" }}>v{detail.version_num}</span>
                <span style={{ marginLeft: "auto", fontSize: ".75rem", color: "var(--color-ink-2)" }}>
                  {formatDate(detail.created_at)}
                </span>
              </div>
              <div style={{ fontSize: ".75rem", color: "var(--color-ink-2)", marginBottom: ".5rem" }}>
                Por {detail.author?.name ?? "sistema"}
                {detail.changed_fields.length > 0 ? <span> · Alterou: {detail.changed_fields.join(", ")}</span> : null}
              </div>
              <pre
                className="mono"
                style={{
                  fontSize: ".75rem",
                  background: "var(--color-soft)",
                  border: ".0625rem solid var(--color-line)",
                  borderRadius: "var(--radius-md)",
                  padding: ".75rem",
                  overflow: "auto",
                  maxHeight: "60vh",
                }}
              >
{JSON.stringify(detail.snapshot, null, 2)}
              </pre>
            </div>
          ) : (
            <EmptyState
              icon={<HistoryIcon className="h-12 w-12" />}
              title="Selecione uma versão"
              description="Clique em 'Ver' para inspecionar o snapshot."
              className="h-full"
            />
          )}
        </section>
      </div>

      <Dialog open={!!rollbackTarget} onOpenChange={(open) => !open && setRollbackTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reverter para v{rollbackTarget?.version_num}?</DialogTitle>
            <DialogDescription>
              Restaura o snapshot {rollbackTarget?.scope} desta versão. Vai criar uma nova versão
              marcada como "revertido de". <strong>Não publica automaticamente no WC</strong>, você
              precisa clicar em "Republicar" depois (RN07).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRollbackTarget(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => rollbackTarget && rollbackMutation.mutate(rollbackTarget)}
              loading={rollbackMutation.isPending}
            >
              Reverter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
